import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, ShieldCheck, AlertTriangle, Mail, FileBarChart, Users, CheckCircle2, XCircle, AlertCircle, RefreshCw, Clock3, Lock } from "lucide-react";
import { toast } from "sonner";
import { ErrorState, LoadingState } from "@/components/States";
import { Btn, inputCls } from "@/components/Primitives";
import { GovBadge } from "@/components/GovBadge";
import { api, confirmAction, formatApiError, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const QUESTIONS = [
  "How ready is this trip?", "What's missing?", "Who hasn't completed onboarding?",
  "Which preparations need attention?", "Which documents are missing?",
];
const FINANCE_QUESTIONS = [
  "Which bookings are unresolved?",
  "How profitable is this trip?",
  "What expense category should I watch?",
  "What should I collect before the trip?",
];
const ICON = { ok: CheckCircle2, warn: AlertCircle, blocker: XCircle, restricted: Lock };
const COLOR = { ok: "text-emerald-600", warn: "text-amber-600", blocker: "text-rose-600", restricted: "text-slate-500" };

export default function CopilotTab({ trip }) {
  const { perms } = useAuth();
  const canSend = perms.includes("participant:manage");
  const canFinance = ["booking:manage", "payment:capture", "payment:refund", "finance:reconcile"].some((p) => perms.includes(p));
  const canReport = perms.includes("finance:reconcile");
  const questions = canFinance ? [...QUESTIONS, ...FINANCE_QUESTIONS] : QUESTIONS;
  const [readiness, setReadiness] = useState(null);
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [asking, setAsking] = useState(false);
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState("");
  const [report, setReport] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [readinessError, setReadinessError] = useState("");
  const [refreshingInsights, setRefreshingInsights] = useState(false);
  const [insightsGeneratedAt, setInsightsGeneratedAt] = useState(null);
  const loadedInsightsTripRef = useRef(null);

  const refreshInsights = useCallback(async ({ automatic = false } = {}) => {
    setReadinessError("");
    setRefreshingInsights(true);
    try {
      const { data } = await api.post("/ai/copilot/ask", { trip_id: trip.id, question: "readiness snapshot" });
      setReadiness(data.grounding);
      setInsightsGeneratedAt(new Date().toISOString());
      if (!automatic) toast.success("Insights refreshed");
    } catch (err) {
      if (automatic) {
        setReadiness(false);
        setReadinessError(formatApiError(err, "Could not load copilot readiness."));
      } else {
        toast.error(formatApiError(err, "Could not refresh insights."));
      }
    } finally {
      setRefreshingInsights(false);
    }
  }, [trip.id]);

  const loadReadiness = useCallback(async () => {
    setReadinessError("");
    try {
      const { data } = await api.get(`/ai/copilot/insights/${trip.id}`);
      if (data.insight?.grounding) {
        setReadiness(data.insight.grounding);
        setInsightsGeneratedAt(data.insight.generated_at || null);
        return;
      }
      await refreshInsights({ automatic: true });
    } catch (err) {
      setReadiness(false);
      setReadinessError(formatApiError(err, "Could not load copilot readiness."));
    }
  }, [refreshInsights, trip.id]);
  useEffect(() => {
    if (loadedInsightsTripRef.current === trip.id) return;
    loadedInsightsTripRef.current = trip.id;
    loadReadiness();
  }, [loadReadiness, trip.id]);

  const ask = async (question) => {
    const text = question || q;
    if (!text.trim()) return;
    setAsking(true); setAnswer(null);
    try { const { data } = await api.post("/ai/copilot/ask", { trip_id: trip.id, question: text }); setAnswer(data.answer); setReadiness(data.grounding); setQ(text); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAsking(false); }
  };
  const makeDraft = async (kind) => {
    setDrafting(kind); setDraft(null);
    try { const { data } = await api.post("/ai/copilot/draft", { trip_id: trip.id, kind }); setDraft(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDrafting(""); }
  };
  const sendDraft = async () => {
    if (!confirmAction("Approve and send this communication?")) return;
    try { const { data } = await api.post(`/ai/copilot/drafts/${draft.id}/send`); toast.success(`Sent to ${data.sent} participant(s)`); setDraft({ ...draft, status: "sent" }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const genReport = async () => {
    setReporting(true); setReport(null);
    try { const { data } = await api.post("/ai/reports/post-trip", { trip_id: trip.id }); setReport(data.report); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setReporting(false); }
  };

  if (readinessError) return <ErrorState message={readinessError} onRetry={loadReadiness} />;
  if (readiness === null) return <LoadingState label="Analysing trip..." />;

  return (
    <div className="space-y-4">
      {readiness && (
        <section className="overflow-hidden rounded-[1.5rem] border border-[#D8E2DC] bg-white shadow-[0_14px_36px_rgba(23,33,29,0.06)]" data-testid="readiness-scorecard">
          <div className="flex flex-col gap-4 border-b border-[#E5EAE6] bg-gradient-to-r from-[#F0F7F3] via-white to-[#FFF9EC] p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-[5px] border-[#BFD9CD] bg-white text-xl font-black text-[#2D7A65] shadow-sm" data-testid="readiness-score">{readiness.readiness_score}%</div>
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-base font-black text-[#17211D]"><ShieldCheck className="h-4 w-4 text-[#2D7A65]" /> Trip readiness</p>
                {readiness.blockers?.length > 0
                  ? <p className="mt-0.5 text-sm font-medium text-rose-600">Blockers: {readiness.blockers.join(", ")}</p>
                  : <p className="mt-0.5 text-sm text-slate-500">No blocking issues. Review the remaining warnings below.</p>}
                {insightsGeneratedAt && <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400"><Clock3 className="h-3 w-3" /> Updated {new Date(insightsGeneratedAt).toLocaleString()}</p>}
              </div>
            </div>
            <Btn variant="secondary" data-testid="refresh-insights-button" onClick={() => refreshInsights()} disabled={refreshingInsights} className="shrink-0">
              {refreshingInsights ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh insights
            </Btn>
          </div>
          <div className="p-4 sm:p-5">
            <div className="mb-4 h-2 overflow-hidden rounded-full bg-[#EEF0EB]">
              <div className="h-full rounded-full bg-gradient-to-r from-[#2D7A65] to-[#E5B85C] transition-[width] duration-500" style={{ width: `${readiness.readiness_score || 0}%` }} />
            </div>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {(readiness.checklist || []).map((c) => { const I = ICON[c.status] || AlertCircle; return (
                <div key={c.key} className="flex min-w-0 items-start justify-between gap-3 rounded-xl border border-[#E5E9E6] bg-[#FCFDFB] p-3 transition-colors hover:bg-[#F7FAF8]">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white ring-1 ring-[#E0E6E2]"><I className={`h-3.5 w-3.5 ${COLOR[c.status] || "text-slate-500"}`} /></span>
                    <div className="min-w-0"><p className="text-xs font-bold text-[#1A2420]">{c.label}</p>{c.detail && <p className="mt-0.5 text-[11px] leading-4 text-slate-500">{c.detail}</p>}</div>
                  </div>
                  <span className={`shrink-0 text-[9px] font-black uppercase tracking-wide ${COLOR[c.status] || "text-slate-500"}`}>{c.status === "ok" ? "Ready" : c.status}</span>
                </div>
              ); })}
            </div>
          </div>
        </section>
      )}

      <section className="rounded-[1.5rem] border border-[#E2DFD7] bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EAF4EF] text-[#2D7A65]"><Sparkles className="h-4 w-4" /></span>
          <div><h3 className="text-base font-black text-[#17211D]">Ask the copilot</h3><p className="mt-0.5 text-xs text-slate-500">Ask about readiness, participants, documents, bookings, or trip finances.</p></div>
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-[#DDDCD6] bg-[#FCFBF8] p-1.5 focus-within:border-[#8EBBA8] focus-within:ring-2 focus-within:ring-[#2D7A65]/10">
          <input data-testid="copilot-input" className={`${inputCls} border-0 bg-transparent shadow-none focus:ring-0`} placeholder="Ask about this trip…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} disabled={asking} />
          <Btn data-testid="copilot-ask-button" onClick={() => ask()} disabled={asking || !q.trim()} className="shrink-0">{asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} <span className="hidden sm:inline">Ask</span></Btn>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {questions.map((question) => <button key={question} data-testid={`copilot-q-${question.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`} onClick={() => ask(question)} disabled={asking} className="rounded-full border border-[#E2DFD7] bg-[#FFFCF7] px-3 py-1.5 text-[11px] font-medium text-[#607069] transition hover:border-[#8EBBA8] hover:bg-[#EEF6F2] hover:text-[#2D7A65] disabled:opacity-50">{question}</button>)}
        </div>
        {answer && <div className="mt-4 rounded-xl border border-[#DCE7E1] bg-[#F3F8F5] p-4 text-sm leading-relaxed text-[#1A2420]" data-testid="copilot-answer"><div className="mb-2"><GovBadge kind="ai" label="Copilot answer" /></div>{answer}</div>}
      </section>

      <div className="grid grid-cols-1 gap-4">
        <section className="rounded-[1.5rem] border border-[#E2DFD7] bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-start gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FFF3D8] text-[#A66B08]"><Mail className="h-4 w-4" /></span><div><h3 className="text-base font-black text-[#17211D]">Draft communications</h3><p className="mt-0.5 text-xs text-slate-500">Create grounded messages for the trip team or participants.</p></div></div>
          <div className="flex flex-wrap gap-2">
            <Btn variant="secondary" data-testid="draft-briefing" onClick={() => makeDraft("briefing")} disabled={!!drafting}>{drafting === "briefing" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Participant briefing</Btn>
            <Btn variant="secondary" data-testid="draft-reminder" onClick={() => makeDraft("reminder_onboarding")} disabled={!!drafting}>{drafting === "reminder_onboarding" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Onboarding reminder</Btn>
            <Btn variant="secondary" data-testid="draft-summary" onClick={() => makeDraft("daily_summary")} disabled={!!drafting}>{drafting === "daily_summary" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Daily summary</Btn>
          </div>
          {draft && (
            <div className="mt-4 rounded-xl border border-[#E2DFD7] bg-[#FCFBF8] p-4" data-testid="copilot-draft">
              <div className="mb-1 flex flex-wrap items-center gap-2"><GovBadge kind="ai" label="AI draft" />{draft.requires_approval && <GovBadge kind="approval" label="Needs approval to send" />}</div>
              <p className="font-semibold text-[#1A2420]">{draft.subject}</p><p className="mt-1 whitespace-pre-line text-sm text-slate-600">{draft.body}</p>
              {draft.requires_approval && <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-[#ECEAE4] pt-3"><span className="flex items-center gap-1 text-xs text-slate-500"><Users className="h-3.5 w-3.5" /> {draft.recipients_count} recipient(s)</span>{draft.status === "sent" ? <span className="flex items-center gap-1 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Sent</span> : canSend ? <Btn data-testid="approve-send-button" onClick={sendDraft} className="h-8 px-3 py-1 text-xs"><Send className="h-3.5 w-3.5" /> Approve & send</Btn> : <span className="flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> Requires a manager to approve</span>}</div>}
            </div>
          )}
        </section>

        {canReport && (
          <section className="rounded-[1.5rem] border border-[#E2DFD7] bg-white p-5 shadow-sm">
            <div className="flex h-full flex-col">
              <div className="mb-4 flex items-start justify-between gap-3"><div className="flex items-start gap-3"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EAF0F7] text-[#45647F]"><FileBarChart className="h-4 w-4" /></span><div><h3 className="text-base font-black text-[#17211D]">Post-trip report</h3><p className="mt-0.5 text-xs text-slate-500">Generate an operational and financial wrap-up.</p></div></div><Btn variant="secondary" data-testid="generate-report-button" onClick={genReport} disabled={reporting} className="shrink-0">{reporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate</Btn></div>
              {report ? <div className="whitespace-pre-line rounded-xl border border-[#DEE5EA] bg-[#F5F8FA] p-4 text-sm leading-relaxed text-[#1A2420]" data-testid="copilot-report">{report}</div> : <div className="flex min-h-24 flex-1 items-center justify-center rounded-xl border border-dashed border-[#D8DEDF] bg-[#FAFBFA] px-4 text-center text-xs text-slate-400">Generate a report after the trip to capture outcomes and financial performance.</div>}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
